import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {LocalStorageService} from './local-storage.service';

const SIGNUP_SOURCE_KEY = 'signupSource';
const EXPIRY_MS = 90 * 24 * 3600 * 1000;
const REFERRER_MAX_LEN = 512;
const UTM_MAX_LEN = 255;

interface SignupSourceEntry {
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  capturedAt: number;
}

export interface SignupSource {
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignupSourceService {

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  constructor(private storageService: LocalStorageService) {}

  /** Returns window.location.search; extracted as a seam for testing. */
  private getSearch(): string {
    return window.location.search;
  }

  /** Returns document.referrer; extracted as a seam for testing. */
  private getReferrer(): string {
    return document.referrer;
  }

  /** Returns window.location.hostname; extracted as a seam for testing. */
  private getLocationHost(): string {
    return window.location.hostname;
  }

  private isExternalReferrer(referrer: string): boolean {
    try {
      return new URL(referrer).hostname !== this.getLocationHost();
    } catch {
      return false;
    }
  }

  private hasValidStoredEntry(): boolean {
    const entry: SignupSourceEntry | null = this.storageService.getItem(SIGNUP_SOURCE_KEY);
    return !!entry && Date.now() - entry.capturedAt <= EXPIRY_MS;
  }

  capture(): void {
    if (!this.isBrowser) {
      return;
    }
    if (this.hasValidStoredEntry()) {
      // First-touch attribution: a prior non-expired capture keeps credit,
      // even if this visit carries a new signal.
      return;
    }
    const params = new URLSearchParams(this.getSearch());
    const utmSource = params.get('utm_source') || undefined;
    const utmMedium = params.get('utm_medium') || undefined;
    const utmCampaign = params.get('utm_campaign') || undefined;
    const hasUtm = !!(utmSource || utmMedium || utmCampaign);

    const referrer = this.getReferrer();
    const isExternalReferrer = !!referrer && this.isExternalReferrer(referrer);

    if (!hasUtm && !isExternalReferrer) {
      // Nothing to record on this visit — do not overwrite a previously stored capture.
      return;
    }

    const entry: SignupSourceEntry = {capturedAt: Date.now()};
    if (isExternalReferrer) {
      entry.referrer = referrer.slice(0, REFERRER_MAX_LEN);
    }
    if (utmSource) {
      entry.utmSource = utmSource.slice(0, UTM_MAX_LEN);
    }
    if (utmMedium) {
      entry.utmMedium = utmMedium.slice(0, UTM_MAX_LEN);
    }
    if (utmCampaign) {
      entry.utmCampaign = utmCampaign.slice(0, UTM_MAX_LEN);
    }

    this.storageService.setItem(SIGNUP_SOURCE_KEY, entry);
  }

  getValidSource(): SignupSource | null {
    if (!this.isBrowser) {
      return null;
    }
    const entry: SignupSourceEntry | null = this.storageService.getItem(SIGNUP_SOURCE_KEY);
    if (!entry) {
      return null;
    }
    if (Date.now() - entry.capturedAt > EXPIRY_MS) {
      this.storageService.removeItem(SIGNUP_SOURCE_KEY);
      return null;
    }
    const {capturedAt, ...source} = entry;
    return source;
  }

  clear(): void {
    this.storageService.removeItem(SIGNUP_SOURCE_KEY);
  }
}
